import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from './client';

export function fetchSyncJob(token, jobId) {
  return apiRequest(`/admin/sync-jobs/${jobId}`, token);
}

/** @param {'products'|'orders'|'orders_unshipped'} kind */
export function fetchLatestSyncJob(token, kind) {
  const query = new URLSearchParams({ kind }).toString();
  return apiRequest(`/admin/sync-jobs/latest?${query}`, token);
}

/** Load + refresh last sync run for Products / Orders / Unshipped banners.
 * Keeps polling while the latest job is queued/running so the UI stays accurate
 * after navigating away and coming back.
 */
export function useLatestSyncJob(token, kind) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!token || !kind) {
      setJob(null);
      setLoading(false);
      return null;
    }

    if (!silent) {
      setLoading(true);
    }
    try {
      const payload = await fetchLatestSyncJob(token, kind);
      const next = payload.job || null;
      setJob(next);
      return next;
    } catch {
      // Banner is non-critical — don't fail the page.
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [token, kind]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const inFlight = job?.status === 'queued' || job?.status === 'running';

  useEffect(() => {
    if (!token || !kind || !inFlight) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      void refresh({ silent: true });
    }, 2000);

    return () => window.clearInterval(timer);
  }, [token, kind, inFlight, refresh]);

  return { job, loading, setJob, refresh, inFlight: Boolean(inFlight) };
}

/**
 * Poll a background sync job until it succeeds or fails.
 * Returns the final job payload from GET /admin/sync-jobs/:id
 */
export async function waitForSyncJob(token, jobId, options = {}) {
  const intervalMs = options.intervalMs ?? 1500;
  const timeoutMs = options.timeoutMs ?? 15 * 60 * 1000;
  const onUpdate = options.onUpdate;
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    const payload = await fetchSyncJob(token, jobId);
    const job = payload.job;

    if (onUpdate) {
      onUpdate(job);
    }

    if (job.status === 'succeeded' || job.status === 'failed') {
      return job;
    }

    await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
  }

  throw new Error('Sync is taking too long. Refresh later — it may still finish in the background.');
}
